import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Button } from '../../ui/button';
import {
  CheckCircle2, XCircle, Sparkles, X, FileText, StickyNote, Sparkle,
} from 'lucide-react';

/**
 * AutoCiteSidebar — right-rail card that surfaces every source added by
 * auto-cite so admins can Accept (dismiss) or Reject (strip marker) in
 * one click, no `[cite:...]` hunting.
 *
 * Props:
 *   result   { lessonId, added, applied:[{doc_id, source_title, source_kind,
 *              source_category, score, on_theme, context}] }
 *   onClose  called after every source has been handled OR the user
 *            manually dismisses the sidebar; parent clears state.
 *   onReloadCourse  optional callback to refresh the workspace so the
 *            editor reflects any markers that were stripped.
 */
const AutoCiteSidebar = ({ result, onClose, onReloadCourse }) => {
  const { API } = useContext(AuthContext);
  // Track per-doc decisions so accepted/rejected rows visibly fade out
  // without disappearing from the list mid-scan (less jarring).
  const [decisions, setDecisions] = useState({}); // doc_id -> 'accepted' | 'rejected'

  if (!result || !result.applied?.length) return null;

  const decide = (docId, verdict) => setDecisions((prev) => ({ ...prev, [docId]: verdict }));

  const accept = (h) => {
    decide(h.doc_id, 'accepted');
    toast.success(`Kept ${h.source_title}`);
  };

  const reject = async (h) => {
    // Optimistic UI — mark rejected immediately, revert on error.
    decide(h.doc_id, 'rejected');
    try {
      await axios.post(`${API}/admin/lms/lessons/${result.lessonId}/reject-citation`, { doc_id: h.doc_id });
      toast.success(`Removed citation to ${h.source_title}`);
      if (onReloadCourse) onReloadCourse();
    } catch (err) {
      // Undo the visual state on failure.
      setDecisions((prev) => ({ ...prev, [h.doc_id]: undefined }));
      toast.error(err?.response?.data?.detail || 'Could not remove the citation');
    }
  };

  const handledCount = Object.keys(decisions).length;
  const totalCount = result.applied.length;

  return (
    <div
      className="fixed right-4 top-24 w-80 max-w-[92vw] max-h-[calc(100vh-140px)] rounded-2xl bg-background border border-primary/30 shadow-2xl z-40 flex flex-col overflow-hidden"
      data-testid="auto-cite-sidebar"
    >
      <div className="p-3 border-b border-border bg-primary/5 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-primary text-[10px] uppercase tracking-widest font-bold">
            <Sparkles className="w-3.5 h-3.5" />
            Auto-cite results
          </div>
          <h3 className="text-sm font-bold mt-0.5">
            {totalCount} source{totalCount === 1 ? '' : 's'} added
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {handledCount}/{totalCount} reviewed — accept to keep, reject to strip.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0"
          data-testid="auto-cite-close"
          title="Dismiss (kept citations stay in the lesson)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {result.applied.map((h) => {
          const verdict = decisions[h.doc_id];
          const isDoc = h.source_kind === 'document';
          const Kind = isDoc ? FileText : StickyNote;
          return (
            <div
              key={h.doc_id}
              className={`rounded-lg border p-2.5 transition-opacity ${
                verdict === 'accepted' ? 'border-emerald-500/40 bg-emerald-500/5 opacity-70'
                : verdict === 'rejected' ? 'border-rose-500/40 bg-rose-500/5 opacity-50'
                : 'border-border bg-background'
              }`}
              data-testid={`auto-cite-row-${h.doc_id}`}
            >
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                    isDoc ? 'bg-sky-500/10 text-sky-700 dark:text-sky-300'
                          : 'bg-amber-500/10 text-amber-800 dark:text-amber-200'
                  }`}
                >
                  <Kind className="w-3 h-3" />
                  {isDoc ? 'Doc' : 'Text'}
                </span>
                {h.source_category && (
                  <span
                    className={`inline-flex rounded-full text-[10px] px-1.5 py-0.5 font-semibold ${
                      h.on_theme
                        ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                        : 'bg-primary/10 text-primary'
                    }`}
                    title={h.on_theme ? 'Matches the course theme — boosted' : 'Different from course theme'}
                    data-testid={`auto-cite-category-${h.doc_id}`}
                  >
                    {h.on_theme && <Sparkle className="w-2.5 h-2.5 inline mr-0.5" />}
                    {h.source_category}
                  </span>
                )}
                <span
                  className="ml-auto text-[10px] font-bold tabular-nums text-primary"
                  title="Similarity score"
                  data-testid={`auto-cite-score-${h.doc_id}`}
                >
                  {Math.round(h.score * 100)}%
                </span>
              </div>
              <p className="text-xs font-semibold leading-snug mb-1 line-clamp-2">
                {h.source_title}
              </p>
              {h.context && (
                <p className="text-[10px] text-muted-foreground italic line-clamp-2 mb-2">
                  “{h.context}”
                </p>
              )}
              {!verdict && (
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => accept(h)}
                    className="h-7 flex-1 text-[11px] border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/10"
                    data-testid={`auto-cite-accept-${h.doc_id}`}
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reject(h)}
                    className="h-7 flex-1 text-[11px] border-rose-500/50 text-rose-700 hover:bg-rose-500/10"
                    data-testid={`auto-cite-reject-${h.doc_id}`}
                  >
                    <XCircle className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </div>
              )}
              {verdict === 'accepted' && (
                <p className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">✓ Kept in lesson</p>
              )}
              {verdict === 'rejected' && (
                <p className="text-[10px] font-semibold text-rose-700 dark:text-rose-300">✗ Removed from lesson</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AutoCiteSidebar;
